import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { Router, ActivatedRoute } from '@angular/router';
import { ApiService } from '../services/api.service';
import { AddressComponent } from './address.component';
import { ContactComponent } from './contact.component';

@Component({
  standalone: true,
  selector: 'app-customer',
  templateUrl: './customer.component.html',
  styleUrl: './customer.component.css',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatExpansionModule,
    MatSelectModule,
    MatCheckboxModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    AddressComponent,
    ContactComponent
  ]
})
export class CustomerComponent implements OnInit {
  customerForm: FormGroup;
  customerId: number | null = null;
  isEditMode: boolean = false;
  loading: boolean = false;
  error: string | null = null;

  priceHistoryDataSource = new MatTableDataSource<any>([]);
  priceHistoryLoading: boolean = false;
  priceHistoryColumns: string[] = ['date', 'catalogNum', 'unitPrice', 'costPerUnit', 'net', 'lotNumber', 'salesOrderNumber'];

  @ViewChild(MatSort) set priceHistorySort(s: MatSort) {
    if (s) { this.priceHistoryDataSource.sort = s; }
  }

  @ViewChild(MatPaginator) set priceHistoryPaginator(p: MatPaginator) {
    if (p) { this.priceHistoryDataSource.paginator = p; }
  }

  applyPriceHistoryFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.priceHistoryDataSource.filter = filterValue.trim().toLowerCase();

    if (this.priceHistoryDataSource.paginator) {
      this.priceHistoryDataSource.paginator.firstPage();
    }
  }

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private apiService: ApiService,
    private cdr: ChangeDetectorRef
  ) {
    this.customerForm = this.fb.group({
      qboCustomerId: [null],
      name: ['', Validators.required],
      addresses: this.fb.array([]),  // Address form array
      contacts: this.fb.array([]),   // Contact form array
      accounts: this.fb.array([])    // Account form array
    });
  }

  // Getter for addresses FormArray
  get addresses(): FormArray {
    return this.customerForm.get('addresses') as FormArray;
  }

  // Getter for contacts FormArray
  get contacts(): FormArray {
    return this.customerForm.get('contacts') as FormArray;
  }

  // Getter for accounts FormArray
  get accounts(): FormArray {
    return this.customerForm.get('accounts') as FormArray;
  }

  readonly accountTypes: string[] = ['Shipping'];

  createAccountGroup(account?: any): FormGroup {
    return this.fb.group({
      accountNumber: [account?.accountNumber || account?.AccountNumber || ''],
      name: [account?.name || account?.Name || '', Validators.required],
      provider: [account?.provider || account?.Provider || ''],
      type: [account?.type || account?.Type || ''],
      isActive: [account?.isActive ?? account?.IsActive ?? true],
      notes: [account?.notes || account?.Notes || '']
    });
  }

  getAccountTitle(index: number): string {
    const account = this.accounts.at(index).value;
    if (!account) return `Account ${index + 1}`;
    const name = account.name || '';
    const provider = account.provider || '';
    const type = account.type || '';
    if (name && provider) return `${provider} - ${name}`;
    if (name) return name;
    if (type) return type;
    return `Account ${index + 1}`;
  }

  // Get display title for address
  getAddressTitle(index: number): string {
    const address = this.addresses.at(index).value;
    if (!address) return `Address ${index + 1}`;
    
    const addressType = address.addressType || address.AddressType || '';
    const line1 = address.addressLine1 || address.AddressLine1 || '';
    const city = address.city || address.City || '';
    const state = address.state || address.State || '';
    
    let baseTitle = '';
    if (line1 && city && state) {
      baseTitle = `${line1}, ${city}, ${state}`;
    } else if (line1 && city) {
      baseTitle = `${line1}, ${city}`;
    } else if (line1) {
      baseTitle = line1;
    } else if (city && state) {
      baseTitle = `${city}, ${state}`;
    } else {
      baseTitle = `Address ${index + 1}`;
    }
    
    return addressType ? `${addressType} - ${baseTitle}` : baseTitle;
  }

  // Get display title for contact
  getContactTitle(index: number): string {
    const contact = this.contacts.at(index).value;
    if (!contact) return `Contact ${index + 1}`;
    
    const firstName = contact.firstName || contact.FirstName || '';
    const lastName = contact.lastName || contact.LastName || '';
    const email = contact.email || contact.Email || '';
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    } else if (email) {
      return email;
    }
    
    return `Contact ${index + 1}`;
  }

  ngOnInit(): void {
    // Check if there's an id parameter in the route
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id && !isNaN(+id)) {
        this.customerId = +id;
        this.isEditMode = false;
        this.loadCustomer(this.customerId);
        this.loadPriceHistory(this.customerId);
      } else {
        this.isEditMode = true;
      }
    });

    console.log('CustomerComponent initialized');
    console.log('isEditMode:', this.isEditMode);
    console.log(this.addresses.length, 'addresses initialized');
    console.log(this.contacts.length, 'contacts initialized');
  }

  enterEditMode(): void {
    this.isEditMode = true;
  }

  loadPriceHistory(customerId: number): void {
    this.priceHistoryLoading = true;
    this.apiService.get<any>(`customer/${customerId}/pricehistory`).subscribe({
      next: (data) => {
        const arr = Array.isArray(data) ? data : (data?.value || data?.data || []);
        this.priceHistoryDataSource.data = arr.map((item: any) => ({
          salesOrderId: item.salesOrderId ?? item.SalesOrderId,
          salesOrderNumber: item.salesOrderNumber ?? item.SalesOrderNumber,
          date: item.date ?? item.Date,
          productId: item.productId ?? item.ProductId,
          catalogNum: item.catalogNum ?? item.CatalogNum,
          unitPrice: item.unitPrice ?? item.UnitPrice ?? 0,
          costPerUnit: item.costPerUnit ?? item.CostPerUnit ?? 0,
          netPercent: item.netPercent ?? item.NetPercent ?? 0,
          lotNumber: item.lotNumber ?? item.LotNumber
        }));
        this.priceHistoryLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading price history:', err);
        this.priceHistoryLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  formatNet(value: number | null | undefined): string {
    const net = value ?? 0;
    return `${net.toFixed(2)}%`;
  }

  goToSalesOrder(salesOrderId: number | null | undefined): void {
    if (salesOrderId) {
      this.router.navigate(['/orders', salesOrderId]);
    }
  }

  goToProduct(productId: number | null | undefined): void {
    if (productId) {
      this.router.navigate(['/products', productId]);
    }
  }

  loadCustomer(id: number): void {
    this.loading = true;
    this.error = null;

    // Clear arrays before (re)loading so cancel/reload always starts fresh
    while (this.addresses.length) this.addresses.removeAt(0);
    while (this.contacts.length) this.contacts.removeAt(0);
    while (this.accounts.length) this.accounts.removeAt(0);

    console.log(`Loading customer with ID: ${id}`);

    this.apiService.get<any>(`customer/${id}`).subscribe({
      next: (data) => {
        console.log('Customer data received:', data);
        
        // Handle both camelCase and PascalCase from API
        const customer = {
          qboCustomerId: data.qBO_Customer_Id || data.QBO_Customer_Id || data.qboCustomerId,
          name: data.name || data.Name
        };

        // Populate the form with the customer data
        this.customerForm.patchValue(customer);

        // Populate addresses array
        const addressesData = data.addresses || data.Addresses || [];
        addressesData.forEach((addr: any) => {
          this.addresses.push(this.fb.control(addr));
        });

        // Populate contacts array
        const contactsData = data.contacts || data.Contacts || [];
        contactsData.forEach((contact: any) => {
          this.contacts.push(this.fb.control(contact));
        });

        // Populate accounts array
        const accountsData = data.externalAccounts || data.ExternalAccounts || [];
        accountsData.forEach((account: any) => {
          this.accounts.push(this.createAccountGroup(account));
        });

        this.loading = false;
        
        console.log('Form populated with customer data');
        // Force change detection
        this.cdr.detectChanges();
        console.log('isEditMode:', this.isEditMode);
        console.log(this.addresses.length, 'addresses initialized');
        console.log(this.contacts.length, 'contacts initialized');

      },
      error: (err) => {
        console.error('Error loading customer:', err);
        
        if (err.status === 404) {
          this.error = `Customer with ID ${id} not found.`;
        } else if (err.status === 401) {
          this.error = 'Unauthorized. Please log in again.';
        } else {
          this.error = `Failed to load customer: ${err.message || 'Unknown error'}`;
        }
        
        this.loading = false;
        // Force change detection
        this.cdr.detectChanges();
      }
    });
  }

  onSubmit(): void {
    if (this.customerForm.valid) {
      const formValue = this.customerForm.getRawValue();
      const customerData: any = {
        QBO_Customer_Id: formValue.qboCustomerId,
        Name: formValue.name,
        Addresses: formValue.addresses,
        Contacts: formValue.contacts,
        ExternalAccounts: formValue.accounts
      };

      this.loading = true;
      this.error = null;

      if (this.customerId) {
        customerData.Id = this.customerId;
        this.apiService.put<any>(`customer/${this.customerId}`, customerData).subscribe({
          next: () => {
            this.loading = false;
            this.router.navigate(['/customers']);
          },
          error: (err) => {
            console.error('Error updating customer:', err);
            this.error = `Failed to update customer: ${err.message || 'Unknown error'}`;
            this.loading = false;
          }
        });
      } else {
        this.apiService.post<any>('customer', customerData).subscribe({
          next: () => {
            this.loading = false;
            this.router.navigate(['/customers']);
          },
          error: (err) => {
            console.error('Error creating customer:', err);
            this.error = `Failed to create customer: ${err.message || 'Unknown error'}`;
            this.loading = false;
          }
        });
      }
    }
  }

  addAddress(): void {
    this.addresses.push(this.fb.control(null));
  }

  removeAddress(index: number): void {
    this.addresses.removeAt(index);
  }

  addContact(): void {
    this.contacts.push(this.fb.control(null));
  }

  removeContact(index: number): void {
    this.contacts.removeAt(index);
  }

  addAccount(): void {
    this.accounts.push(this.createAccountGroup());
  }

  removeAccount(index: number): void {
    this.accounts.removeAt(index);
  }

  onCancel(): void {
    if (this.customerId) {
      this.isEditMode = false;
      this.loadCustomer(this.customerId);
    } else {
      this.router.navigate(['/customers']);
    }
  }
}
